import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import StarIcon from '@mui/icons-material/Star';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';

import ShiftDetail from '../reports/ShiftDetail';
import BonusDetail from '../reports/BonusDetail';
import RaffleDetail from '../reports/RaffleDetail';
import PointsDetail from '../reports/PointsDetail';
import TicketOutDetail from '../reports/TicketOutDetail';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

import { TOKENS, shiftDurationLabel, formatHoursWorked } from '../theme';
import { deriveAssignedDay } from './EmployeeSessionsScreen';

const TITLE = {
  shift: 'Shift Report',
  bonus: 'Bonus Report',
  raffle: 'Raffle Report',
  points: 'Points Report',
  ticket: 'Ticket Out Report',
};

const ACCENT = {
  shift: {
    bg: TOKENS.blue50,
    color: TOKENS.blue700,
    border: TOKENS.blue200,
    Icon: DescriptionIcon,
  },
  bonus: {
    bg: TOKENS.amber50,
    color: TOKENS.amber700,
    border: TOKENS.amber200,
    Icon: CardGiftcardIcon,
  },
  raffle: {
    bg: TOKENS.pink50,
    color: TOKENS.pink700,
    border: TOKENS.pink200,
    Icon: EmojiEventsIcon,
  },
  points: {
    bg: TOKENS.emerald50,
    color: TOKENS.emerald700,
    border: TOKENS.emerald200,
    Icon: StarIcon,
  },
  ticket: {
    bg: TOKENS.violet50,
    color: TOKENS.violet700,
    border: TOKENS.violet200,
    Icon: ConfirmationNumberIcon,
  },
};

export default function ReportDetailScreen({
  type,
  session,
  sessionData,
  sessionNumber,
  customers,
  manageBonusList,
  categories,
  employees,
  onNavigateReport,
  onEditBonus,
  onDeleteBonus,
  onAddBonus,
  onSubmitBonusDecision,
  onEditRaffle,
  onDeleteRaffle,
  onSubmitRaffleDecision,
  onSubmitPointDecision,
  onEditTicketOut,
  onDeleteTicketOut,
  onEditTransaction,
  onDeleteTransaction,
  onAddTransaction,
  onEditCashLeft,
  onAuditComplete,
}) {
  const accent = ACCENT[type] || ACCENT.shift;
  const sessionMeta = {
    id: sessionNumber || '—',
    employee: session?.name,
    clockIn: session?.clockIn || session?.timeIn,
    clockOut: session?.clockOut || session?.timeOut,
    assignedDay: deriveAssignedDay(session),
  };
  const durationLabel =
    formatHoursWorked(session?.hoursWorked) || shiftDurationLabel(session);

  return (
    <Box sx={{ pb: 4 }}>
      <Box sx={{ px: 2, pt: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 1.5,
              background: accent.bg,
              border: `1px solid ${accent.border}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <accent.Icon sx={{ color: accent.color, fontSize: 26 }} />
          </Box>
          <Box>
            <Typography
              sx={{ fontSize: 20, fontWeight: 700, color: TOKENS.slate900 }}
            >
              {TITLE[type]}
            </Typography>
            <Typography sx={{ fontSize: 13, color: TOKENS.slate500 }}>
              {sessionMeta.employee} · {sessionMeta.clockIn}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
          <Chip
            size="small"
            label={sessionMeta.assignedDay}
            sx={{
              height: 22,
              fontSize: 12,
              fontWeight: 700,
              textTransform: 'uppercase',
              background: TOKENS.blue50,
              color: TOKENS.blue700,
              border: `1px solid ${TOKENS.blue200}`,
            }}
          />
          {durationLabel && (
            <Chip
              size="small"
              icon={
                <AccessTimeIcon
                  style={{ color: TOKENS.emerald700, fontSize: 14 }}
                />
              }
              label={`${durationLabel} worked`}
              sx={{
                height: 22,
                fontSize: 12,
                fontWeight: 700,
                background: TOKENS.emerald50,
                color: TOKENS.emerald700,
                border: `1px solid ${TOKENS.emerald200}`,
              }}
            />
          )}
        </Box>
      </Box>
      <Box sx={{ px: 2, mt: 2 }}>
        {type === 'shift' && (
          <ShiftDetail
            session={session}
            sessionData={sessionData}
            customers={customers}
            employees={employees}
            categories={categories}
            manageBonusList={manageBonusList}
            onEditTransaction={onEditTransaction}
            onDeleteTransaction={onDeleteTransaction}
            onAddTransaction={onAddTransaction}
            onEditCashLeft={onEditCashLeft}
            onNavigateReport={onNavigateReport}
          />
        )}
        {type === 'bonus' && (
          <BonusDetail
            session={session}
            sessionData={sessionData}
            customers={customers}
            manageBonusList={manageBonusList}
            onEditBonus={onEditBonus}
            onDeleteBonus={onDeleteBonus}
            onAddBonus={onAddBonus}
            onSubmitDecision={onSubmitBonusDecision}
            onAuditComplete={onAuditComplete}
          />
        )}
        {type === 'raffle' && (
          <RaffleDetail
            session={session}
            sessionData={sessionData}
            customers={customers}
            onEditRaffle={onEditRaffle}
            onDeleteRaffle={onDeleteRaffle}
            onSubmitDecision={onSubmitRaffleDecision}
            onAuditComplete={onAuditComplete}
          />
        )}
        {type === 'points' && (
          <PointsDetail
            sessionData={sessionData}
            onSubmitDecision={onSubmitPointDecision}
            onAuditComplete={onAuditComplete}
          />
        )}
        {type === 'ticket' && (
          <TicketOutDetail
            sessionData={sessionData}
            onEditTicketOut={onEditTicketOut}
            onDeleteTicketOut={onDeleteTicketOut}
          />
        )}
      </Box>
    </Box>
  );
}
